import React, { useMemo, useState } from "react";
import { Breadcrumb, Button, Input, Select, Table, Tooltip } from "antd";
import { InfoCircleFilled, MinusSquareFilled, PlusSquareFilled } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { t } from "i18next";

const EMPTY_ROW = {
  itemDescription: "",
  approvedMake: "",
  qty: "",
  uom: undefined,
  unitRate: "",
  remarks: "",
};

const UOM_OPTIONS = [
  { value: "Nos", label: "Nos" },
  { value: "Kg", label: "Kg" },
  { value: "MT", label: "MT" },
  { value: "Bag", label: "Bag" },
  { value: "Meter", label: "Meter" },
  { value: "Sqm", label: "Sqm" },
  { value: "Cum", label: "Cum" },
  { value: "Ltr", label: "Ltr" },
];

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function limitToDigits(value, maxDigits, allowDecimal = true) {
  if (value == null) return "";
  let v = String(value);
  v = allowDecimal
    ? v
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/^\./, "0.")
    : v.replace(/[^0-9]/g, "");

  const parts = v.split(".");
  const intPart = (parts[0] || "").replace(/^0+(?=\d)/, "0");
  const decPart = parts[1] || "";

  // enforce digit budget across int + decimal parts (ignore dot)
  const digits = (intPart + decPart).replace(/\D/g, "");
  if (digits.length <= maxDigits) return v;

  const trimmedDigits = digits.slice(0, maxDigits);
  // rebuild keeping original decimal placement if any; otherwise just return trimmed digits
  if (!allowDecimal || parts.length === 1) return trimmedDigits;

  // allocate digits back to int/dec based on original int length (at least 1 digit)
  const desiredIntLen = Math.max(1, Math.min(intPart.replace(/\D/g, "").length, trimmedDigits.length));
  const newInt = trimmedDigits.slice(0, desiredIntLen);
  const newDec = trimmedDigits.slice(desiredIntLen);
  return newDec.length ? `${newInt}.${newDec}` : newInt;
}

const GeneralProcurementEntry = () => {
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const addRowAfter = (index) => {
    setRows((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, { ...EMPTY_ROW });
      return next;
    });
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const dataSource = useMemo(
    () =>
      rows.map((r, index) => {
        const qty = toNumberOrZero(r.qty);
        const unitRate = toNumberOrZero(r.unitRate);
        const amount = qty * unitRate;
        return {
          key: String(index),
          index,
          ...r,
          amount,
        };
      }),
    [rows]
  );

  const grandTotal = useMemo(
    () => dataSource.reduce((sum, r) => sum + (r.amount || 0), 0),
    [dataSource]
  );

  const columns = useMemo(
    () => [
      {
        title: "Sr.",
        dataIndex: "index",
        width: 80,
        render: (_, record) => record.index + 1,
      },
      {
        title: "Item Description",
        dataIndex: "itemDescription",
        render: (_, record) => (
          <Input
            value={record.itemDescription}
            placeholder="Enter item description"
            onChange={(e) =>
              updateRow(record.index, { itemDescription: e.target.value })
            }
          />
        ),
      },
      {
        title: "Approved Make",
        dataIndex: "approvedMake",
        width: 180,
        render: (_, record) => (
          <Input
            value={record.approvedMake}
            placeholder="Enter approved make"
            onChange={(e) =>
              updateRow(record.index, { approvedMake: e.target.value })
            }
          />
        ),
      },
      {
        title: "Qty.",
        dataIndex: "qty",
        width: 120,
        render: (_, record) => (
          <Input
            value={record.qty}
            inputMode="decimal"
            maxLength={6}
            placeholder="0"
            onChange={(e) => {
              const value = limitToDigits(e.target.value, 4, true);
              updateRow(record.index, { qty: value });
            }}
          />
        ),
      },
      {
        title: "UoM.",
        dataIndex: "uom",
        width: 140,
        render: (_, record) => (
          <Select
            style={{ width: "100%" }}
            value={record.uom}
            placeholder="Select"
            options={UOM_OPTIONS}
            onChange={(value) => updateRow(record.index, { uom: value })}
          />
        ),
      },
      {
        title: "Unit Rate",
        dataIndex: "unitRate",
        width: 140,
        render: (_, record) => (
          <Input
            value={record.unitRate}
            inputMode="decimal"
            maxLength={6}
            placeholder="0"
            onChange={(e) => {
              const value = limitToDigits(e.target.value, 6, true);
              updateRow(record.index, { unitRate: value });
            }}
          />
        ),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        width: 140,
        align: "right",
        render: (_, record) => (
          <Input
            style={{ textAlign: "right" }}
            value={Number(record.amount || 0).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
            readOnly
          />
        ),
      },
      {
        title: "Remarks from Buyer (if any)",
        dataIndex: "remarks",
        width: 220,
        render: (_, record) => (
          <Input
            value={record.remarks}
            placeholder="Enter remarks"
            onChange={(e) => updateRow(record.index, { remarks: e.target.value })}
          />
        ),
      },
      {
        title: "",
        key: "actions",
        width: 90,
        fixed: "right",
        render: (_, record) => (
          <div style={{ display: "flex", gap: 10 }}>
            <Tooltip title="Add row">
              <Button
                type="text"
                aria-label="Add row"
                icon={<PlusSquareFilled />}
                onClick={() => addRowAfter(record.index)}
              />
            </Tooltip>
            <Tooltip title="Delete row">
              <Button
                type="text"
                danger
                disabled={rows.length <= 1}
                aria-label="Delete row"
                icon={<MinusSquareFilled />}
                onClick={() => removeRow(record.index)}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    [rows.length]
  );

  return (
    <>
    <div className="ListingTopBar">
            <div className="pagenamewrap">
              <div className="pagename">
                <h3>
                  Purchase Requisition (PR) -{" "}
                  
                </h3>
    
                <Breadcrumb className="Breadcrumb-Sec">
                  <Breadcrumb.Item>
                    <Link to="/purchase-requisition-history">{t("home")}</Link>
                  </Breadcrumb.Item>
                  
                </Breadcrumb>
              </div>
              <Tooltip
                color={"#094E8F"}
                placement="left"
                title={
                  <span style={{ color: "#fff" }}>
                    The unit rate or price shown in PR is estimated price for
                    calculating PR workflows. Citywise actual buying rates can vary.
                  </span>
                }
              >
                <Button
                  className="SaveGradiantBtn"
                  // onClick={handleSubmit}
                  // loading={btnLoading}
                >
                  {/* Save */}
                  <InfoCircleFilled />
                </Button>
              </Tooltip>
            </div>
          </div>
    <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body tbl-collapse">
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: 1200 }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1} colSpan={5} style={{ textAlign: "right" }}>
                <div
                  className="totalCityOverallSitee5"
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    letterSpacing: "1px",
                  }}
                >
                  Total
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell
                index={6}
                align="right"
              >
                <div className="totalCityOverallSitee5" style={{ fontWeight: "bold" }}>
                  ₹{" "}
                  {grandTotal.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
              <Table.Summary.Cell index={8} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
        </div>
        </section>
    </>
  );
};

export default GeneralProcurementEntry;